import { useParams, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, User, Phone, Building, Shield, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { GenerateLoadingOrError } from "../../lib/utils";
import { useOrganizationMembers } from "@/lib/api/betterAuth";
import { PermissionsEditor } from "./components/PermissionsEditor";
import { CustomerAssignmentEditor } from "./components/CustomerAssignmentEditor";

export function BetterAuthPermissionsUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // Get organizationId from state (passed from listing page) or default to fetching
  const organizationId = location.state?.organizationId;

  const membersRes = useOrganizationMembers(organizationId);

  const loadingOrErrorUi = GenerateLoadingOrError([membersRes]);
  if (loadingOrErrorUi) return loadingOrErrorUi;

  const members = membersRes?.data?.data || [];
  const member = members.find((m: any) => m.id === userId);

  if (!member) {
    return (
      <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/permissions/mobile")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Mobile Permissions
          </Button>
        </div>
        <div className="text-center py-8">
          <p className="text-gray-500">User not found.</p>
        </div>
      </section>
    );
  }

  const handlePermissionsUpdate = () => {
    // Refresh the data
    membersRes.refetch();
  };

  const handleCustomerAssignmentUpdate = () => {
    // Refresh the data
    membersRes.refetch();
  };

  // Determine display role
  const displayRole = (member.authuser?.user_id || member.organization?.org_type === "internal") ? "Internal User" : member.role;

  return (
    <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/permissions/mobile")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Mobile Permissions
        </Button>
      </div>

      <div className="max-w-7xl mx-auto w-full space-y-6">
        {/* User Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              User Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">Customer Name</p>
                  <p className="text-sm text-gray-600">
                    {member.authuser?.name || "Unknown"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">Phone Number</p>
                  <p className="text-sm text-gray-600">
                    {member.authuser?.phoneNumber || "No phone"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Shield className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">Role</p>
                  <p className="text-sm text-gray-600 capitalize">
                    {displayRole}
                  </p>
                </div>
              </div>

              {member.organization && (
                <div className="flex items-center gap-3">
                  <Building className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium">Organization ID</p>
                    <p className="text-sm text-gray-600 font-mono">
                      {member.organization!.name}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Separator />

        {/* Permissions and Customer Assignment Sections - Side by side on larger screens */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Permissions Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Permissions Management
              </CardTitle>
              <p className="text-sm text-gray-600">
                Manage user permissions for mobile applications. Changes are saved automatically.
              </p>
            </CardHeader>
            <CardContent>
              <PermissionsEditor
                member={member}
                organizationId={organizationId}
                onSuccess={handlePermissionsUpdate}
                inline={true}
              />
            </CardContent>
          </Card>

          {/* Customer Assignment Section */}
          <Card className="flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Risk Profile Assignments
              </CardTitle>
              <p className="text-sm text-gray-600">
                Assign risk customers to this user. Changes are saved when you click Save Changes.
              </p>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col min-h-0">
              <CustomerAssignmentEditor
                member={member}
                relationType="risk"
                onSuccess={handleCustomerAssignmentUpdate}
                inline={true}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

export default BetterAuthPermissionsUserDetailPage;
